import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Cookie } from 'lucide-react';
import SEO from '@/components/SEO';

export const CookiePolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Cookie Policy"
        description="Learn about how BMI Calc Hub uses cookies and local storage. Manage your cookie preferences."
        canonical="/cookies"
      />
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Calculator
          </Button>
        </Link>

        <Card className="card-elevated">
          <CardContent className="p-8 sm:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Cookie Policy</h1>
                <p className="text-sm text-muted-foreground">Last updated: January 2025</p>
              </div>
            </div>

            <div className="prose prose-sm max-w-none text-muted-foreground space-y-6">
              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">1. What Are Cookies?</h2>
                <p>
                  Cookies are small text files that are stored on your device (computer, tablet, or mobile) when 
                  you visit a website. They help the website remember your preferences and improve your experience.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">2. How We Use Cookies</h2>
                <p>BMI Calc Hub uses cookies for the following purposes:</p>
                
                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">2.1 Essential Cookies</h3>
                <p>These cookies are necessary for the website to function properly:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Session Cookies:</strong> Keep you logged in during your visit</li>
                  <li><strong>Authentication Cookies:</strong> Remember your login status</li>
                  <li><strong>Security Cookies:</strong> Protect against unauthorized access</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">2.2 Functional Cookies</h3>
                <p>These cookies remember your preferences:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Theme Preference:</strong> Remember your light/dark mode choice</li>
                  <li><strong>Unit Preference:</strong> Remember metric/US unit selection</li>
                  <li><strong>Language Settings:</strong> Remember your language preference</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">2.3 Analytics Cookies</h3>
                <p>These cookies help us understand how visitors use our site:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Pages visited and time spent</li>
                  <li>Features used most frequently</li>
                  <li>Error reports and performance metrics</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">3. Types of Cookies We Use</h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse mt-4">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-semibold text-foreground">Cookie Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground">Purpose</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border">
                        <td className="py-3 px-4">session_token</td>
                        <td className="py-3 px-4">User authentication</td>
                        <td className="py-3 px-4">7 days</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-4">theme</td>
                        <td className="py-3 px-4">Theme preference (light/dark)</td>
                        <td className="py-3 px-4">1 year</td>
                      </tr>
                      <tr className="border-b border-border">
                        <td className="py-3 px-4">cookie_consent</td>
                        <td className="py-3 px-4">Remember cookie preferences</td>
                        <td className="py-3 px-4">1 year</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">4. Local Storage</h2>
                <p>
                  In addition to cookies, we use browser local storage to save your BMI calculation history 
                  if you're not logged in. This data:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Is stored only on your device</li>
                  <li>Is never sent to our servers</li>
                  <li>Can be cleared through your browser settings</li>
                  <li>Persists until you manually clear it</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">5. Third-Party Cookies</h2>
                <p>
                  We may use third-party services that set their own cookies:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Google Analytics:</strong> For website analytics (if enabled)</li>
                  <li><strong>Google AdSense:</strong> For displaying relevant advertisements (if enabled)</li>
                </ul>
                <p className="mt-4">
                  These third parties have their own privacy policies governing the use of cookies.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">6. Managing Cookies</h2>
                <p>You can control cookies through several methods:</p>
                
                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">6.1 Browser Settings</h3>
                <p>
                  Most browsers allow you to block or delete cookies through their settings:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                  <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Cookies</li>
                  <li><strong>Edge:</strong> Settings → Privacy → Cookies</li>
                </ul>

                <h3 className="text-lg font-medium text-foreground mt-6 mb-3">6.2 Opt-Out Links</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Google Analytics: <a href="https://tools.google.com/dlpage/gaoptout" className="text-primary hover:underline">https://tools.google.com/dlpage/gaoptout</a></li>
                  <li>Google Ads: <a href="https://adssettings.google.com" className="text-primary hover:underline">https://adssettings.google.com</a></li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">7. Impact of Disabling Cookies</h2>
                <p>
                  If you disable cookies, some features may not work properly:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You may need to log in each time you visit</li>
                  <li>Your preferences won't be saved</li>
                  <li>Some functionality may be limited</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">8. Updates to This Policy</h2>
                <p>
                  We may update this Cookie Policy from time to time. Any changes will be posted on this page 
                  with an updated "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-foreground mt-8 mb-4">9. Contact Us</h2>
                <p>
                  If you have questions about our use of cookies, please contact us at:
                </p>
                <p className="mt-2">
                  <strong>Email:</strong> info@bmicalchub.com<br />
                  <strong>Website:</strong> bmicalchub.com
                </p>
              </section>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default CookiePolicyPage;
